import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spin, Statistic, Typography } from 'antd';
import { errorMessage } from '../api';
import type { BillingApi, Revenue } from '../billingApi';
import { MonthBars } from '../components/MonthBars';
import { formatMoney } from '../lib/format';

/** "42 %" of the trials that ended; null while none has ended. */
export function conversionRate(
  r: Pick<Revenue, 'trials_ended' | 'trials_converted'>
): number | null {
  return r.trials_ended > 0 ? Math.round((r.trials_converted / r.trials_ended) * 100) : null;
}

/** MRR per currency, joined ("€12.00 + $5.00"); zero euros while nothing is paid. */
export function mrrText(mrr: Revenue['mrr']): string {
  return mrr.length
    ? mrr.map((m) => formatMoney(m.amount, m.currency)).join(' + ')
    : formatMoney(0, 'EUR');
}

/** The numbers the owner looks at first: subscribers, trials, conversion, MRR, and the 12 months. */
export const RevenueScreen: React.FC<{ billing: BillingApi }> = ({ billing }) => {
  const [data, setData] = useState<Revenue | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    billing
      .revenue()
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setLoadError(null);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [billing, reloadKey]);

  const rate = data ? conversionRate(data) : null;

  return (
    <div>
      <Typography.Title level={3} style={{ margin: '0 0 16px' }}>
        Revenue
      </Typography.Title>

      {loadError && (
        <Alert
          type="error"
          showIcon
          title="Could not load the revenue"
          description={loadError}
          action={<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
        />
      )}
      {!data && !loadError && <Spin style={{ display: 'block', marginTop: 48 }} />}

      {data && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={8}>
              <Card>
                <Statistic title="Active subscribers" value={data.active_subscribers} />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card>
                <Statistic title="Subscribed in trial" value={data.subscribed_in_trial} />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card>
                <Statistic title="Trials running" value={data.trials_running} />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card>
                <Statistic
                  title="Trial to paid"
                  value={rate === null ? '–' : `${rate} %`}
                  suffix={
                    rate === null ? undefined : (
                      <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                        {data.trials_converted} of {data.trials_ended}
                      </Typography.Text>
                    )
                  }
                />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card>
                <Statistic title="MRR (net of VAT)" value={mrrText(data.mrr)} />
              </Card>
            </Col>
          </Row>

          <Card title="New and cancelled, per month" style={{ marginTop: 16 }}>
            <MonthBars months={data.months} />
          </Card>
        </>
      )}
    </div>
  );
};
